import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import axiosClient from "../api/api";
import { getContrastTextColor } from "../utils/themeUtils";

interface BrandingData {
  logo_url: string;
  primary_color: string;
  secondary_color: string;
}

interface ThemeContextType {
  branding: BrandingData | null;
  loading: boolean;
  error: string | null;
  updateBranding: (data: BrandingData) => void;
  fetchBranding: (id?: string, forceRefresh?: boolean) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [retryCount, setRetryCount] = useState<number>(0);

  const updateBranding = (data: BrandingData) => {
    setBranding(data);
    // Update CSS variables
    document.documentElement.style.setProperty("--primary-color", data.primary_color);
    document.documentElement.style.setProperty("--secondary-color", data.secondary_color);
    document.documentElement.style.setProperty("--secondary-contrast-text", getContrastTextColor(data.secondary_color));
  };

  const fetchBranding = useCallback(
    async (id?: string, forceRefresh: boolean = false) => {
      try {
        const clientId = id || getClientId();
        console.log("ThemeContext: fetchBranding called with id:", id, "clientId:", clientId, "forceRefresh:", forceRefresh);

        // No rate limiting - call API every time for fresh branding

        // Prevent infinite retries (max 3 retries with exponential backoff)
        if (retryCount >= 3) {
          console.warn("Max retry attempts reached for branding API");
          return;
        }

        setLoading(true);
        setError(null);

        // No caching - always fetch fresh branding data

        console.log("ThemeContext: Making API call to branding endpoint with clientId:", clientId);
        const response = await axiosClient.get(`/retailerProduct/api/settings/catalogue/branding`, {
          params: {
            login_id: clientId,
          },
        });
        console.log("ThemeContext: API response:", response.data);

        if (response.data && response.data.success) {
          const brandingData: BrandingData = {
            logo_url: response.data.data.logo_url || "",
            primary_color: response.data.data.primary_color || "#24588C",
            secondary_color: response.data.data.secondary_color || "#16A34A",
          };

          // No caching - fresh data every time

          updateBranding(brandingData);
          setLastFetchTime(Date.now());
          setRetryCount(0); // Reset retry count on success
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching client branding:", err);
        setError("Failed to load branding");
        setRetryCount((prev) => prev + 1);

        // Set default branding only if we don't have any branding yet
        if (!branding) {
          const defaultBranding: BrandingData = {
            logo_url: "",
            primary_color: "#24588C",
            secondary_color: "#16A34A",
          };
          updateBranding(defaultBranding);
        }
      } finally {
        setLoading(false);
      }
    },
    [lastFetchTime, retryCount, branding]
  );

  // Get ID from URL params or subdomain
  const getClientId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get("id");

    if (idParam) {
      return idParam;
    }

    // Check subdomain
    const hostname = window.location.hostname;
    const subdomain = hostname.split(".")[0];

    if (subdomain && subdomain !== "www" && subdomain !== "localhost") {
      return subdomain;
    }

    return null;
  };

  useEffect(() => {
    const clientId = getClientId();

    if (clientId) {
      // Fetch branding with the client ID
      fetchBranding(clientId);
    } else {
      // No client ID found, use default branding
      const defaultBranding: BrandingData = {
        logo_url: "",
        primary_color: "#24588C",
        secondary_color: "#16A34A",
      };
      updateBranding(defaultBranding);
      setLoading(false);
    }
  }, []);

  // Simplified approach - branding is now handled by NavbarComponent
  // This ensures branding is fetched on every route change in a stable way

  const value: ThemeContextType = {
    branding,
    loading,
    error,
    updateBranding,
    fetchBranding,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
